import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilterData as setFilterDataInStore } from "../../../store/slices/filterDataSlice";
import axiosInstance from "../../../api/Axios";
import axios from "axios";

export default function useCommonListing({
  userInfo,
  passSize = 15,
  passPage = 1,
  passURL = "",
  loadAgain = false,
  includeStatus = true,
  takeFromData = (res) => res?.data?.results,
  takeCountFrom = (res) => res?.data?.count,
  useOldPagination = false,
  persist = false,
  listId = null,
  noPaginationAndFilters = false,
  skip = false,
  passFilterUrl = "",
}) {
  const dispatch = useDispatch();

  // Default filter state
  const defaultFilterData = {
    pageSize: passSize,
    currentPage: passPage,
    url: passURL,
    search: "",
    active: true,
    filter_url: passFilterUrl,
    sort_column: null,
    sort_order: null,
  };

  // Get saved filters from Redux (if any)
  const savedFilterData = useSelector((state) =>
    listId ? state.filterData[listId] : null
  );

  const [filterData, setFilterData] = useState(defaultFilterData);
  const [searchTerm, setSearchTerm] = useState(""); // Local state for immediate UI feedback
  const [data, setData] = useState([]);
  const [itemsLength, setItemsLength] = useState(0);
  const [loading, setLoading] = useState(true);
  const [classification, setClassification] = useState(null);
  const [brokerName, setBrokerName] = useState(null);
  const [isHydrated, setIsHydrated] = useState(!persist); // only wait when persist is true

  const page = useOldPagination ? "page_num" : "page";
  const size = useOldPagination ? "items" : "page_size";

  // 🧠 Hydrate from Redux store (persist mode)
  useEffect(() => {
    if (persist && listId && savedFilterData) {
      setFilterData((prev) => ({ ...prev, ...savedFilterData }));
      setSearchTerm(savedFilterData.search || "");
      console.log("Hydrated from store:", listId, savedFilterData);
    }
    setIsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 💾 Save to Redux store when filterData changes (persist mode only)
  useEffect(() => {
    if (persist && listId && isHydrated) {
      const dataToStore = {
        ...filterData,
        sort_column: null,
        sort_order: null,
      };
      dispatch(setFilterDataInStore({ listId, data: dataToStore }));
      console.log("Persisted to store:", listId, dataToStore);
    }
  }, [filterData, persist, listId, dispatch, isHydrated]);

  // ⏱️ Debounce search term changes
  useEffect(() => {
    if (!isHydrated) return;
    
    // Don't debounce if the search term hasn't changed from filterData
    if (searchTerm === filterData.search) return;

    const handler = setTimeout(() => {
      setFilterData((prev) => ({
        ...prev,
        search: searchTerm,
        currentPage: 1,
      }));
    }, 500); // 500ms delay

    return () => clearTimeout(handler);
  }, [searchTerm, isHydrated]); // intentionally exclude filterData.search from deps

  // 📡 Fetch API when filters change
  const isEmptyObject = (obj) =>
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0;

  useEffect(() => {
    if (!isHydrated || skip || !filterData.url) {
      if (skip || !filterData.url) setLoading(false);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    let passUrl = filterData.url;
    const joiner = passUrl.includes("?") ? "&" : "?";
    const statusParam = includeStatus
      ? `&status=${filterData.active ? "True" : "False"}`
      : "";

    if (noPaginationAndFilters) {
      passUrl = filterData.url;
    } else {
      if (filterData.search?.length > 0) {
        if (filterData.search.length > 1) {
          passUrl += `${joiner}${page}=${filterData.currentPage}&${size}=${filterData.pageSize}&search=${filterData.search}${statusParam}`;
          if (filterData.filter_url) passUrl += `&${filterData.filter_url}`;
        } else {
          return; // stop if search is only 1 char
        }
      } else {
        passUrl += `${joiner}${page}=${filterData.currentPage}&${size}=${filterData.pageSize}${statusParam}`;
        if (filterData.filter_url) passUrl += `&${filterData.filter_url}`;
      }

      if (filterData.sort_column && filterData.sort_order) {
        passUrl += `&sort_column=${filterData.sort_column}&sort_order=${filterData.sort_order}`;
      }
    }
    setLoading(true);
    axiosInstance
      .get(passUrl, { signal })
      .then((res) => {
        const extracted = takeFromData(res);
        setData(isEmptyObject(extracted) ? [] : extracted);
        setItemsLength(takeCountFrom(res) || 0);
        // Extract classification from API response
        if (res?.data?.classification) {
          setClassification(res.data.classification);
        }
        if (res?.data?.broker_name) {
          setBrokerName(res.data.broker_name);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          console.log("Request canceled:", passUrl);
        } else {
          console.error("API error:", err);
          // Only stop loading if NOT canceled (if canceled, subsequent request will handle loading)
          setLoading(false);
        }
      })
      .finally(() => {
        if (!signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, isHydrated, loadAgain, userInfo?.data?.organization?.id]);

  // 🧩 Pagination + Filter handlers
  const onShowSizeChange = (current, size) => {
    setFilterData((prev) => ({ ...prev, pageSize: size }));
  };

  const onPageChange = (page, pageSize) => {
    setFilterData((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSwitchChange = (event) => {
    setSearchTerm("");
    setFilterData((prev) => ({
      ...prev,
      search: "",
      currentPage: 1,
      active: event,
    }));
  };

  const handleClearAll = () => {
    setSearchTerm("");
    setFilterData({ ...defaultFilterData });
  };

  const setFilterUrl = useCallback((generatedFilterUrl) => {
    setSearchTerm("");
    setFilterData((prev) => {
      if (
        prev.filter_url === generatedFilterUrl &&
        prev.search === "" &&
        prev.currentPage === 1
      ) {
        return prev;
      }
      return {
        ...prev,
        search: "",
        currentPage: 1,
        filter_url: generatedFilterUrl,
      };
    });
  }, []);

  return {
    data,
    itemsLength,
    loading,
    filterData: { ...filterData, search: searchTerm }, // Return searchTerm as filterData.search for responsive UI
    setFilterData,
    onShowSizeChange,
    onPageChange,
    handleSearchChange,
    handleSwitchChange,
    handleClearAll,
    setFilterUrl,
    classification,
    setLoading,
    brokerName,
  };
}
